import { trackCompleteRegistrationEvent } from "@helpers/facebookTracking"
import { getUserId } from "@helpers/sessionStorage"
import { signUp } from "@helpers/supabase"
import * as Sentry from "@sentry/react"
import { setUserId } from "@snowplow/browser-tracker"

type CreateAccountResult = "created" | "alreadyRegistered" | "failed"

/**
 * Creates the account on the step that asks for the password, so the
 * password is never kept between steps (nor in storage, which some browsers
 * block). The visitor goes on signed in: Supabase confirms web sign-ups right
 * away (mailer autoconfirm) and returns the session.
 */
export default async function createAccount({
  email,
  password,
}: {
  email: string
  password: string
}): Promise<CreateAccountResult> {
  const { error } = await signUp({ email, password })

  if (error) {
    return error.code === "user_already_exists" ||
      error.message === "User already registered"
      ? "alreadyRegistered"
      : "failed"
  }

  // NOTE: The account exists from here on: tracking that fails (Facebook Pixel
  // blocked, a network error) must not keep the visitor on this step.
  try {
    const userId = await getUserId()

    if (userId) setUserId(userId)

    await trackCompleteRegistrationEvent()
  } catch (trackingError) {
    console.error(trackingError)
    Sentry.captureException(trackingError, { level: "warning" })
  }

  return "created"
}
